import React, { useEffect, useState } from "react";
import { ConnectMovmoButton } from "@movmo/connect-button";
import "@movmo/connect-button/dist/index.css";
import {
    applyWireTraveler,
    useLinkedTravelerState,
} from "../../hooks/linkedTravelerStore";
import { buildRedirectUri } from "../../config/movmoIdentity";
import {
    DemoAirlineLoyaltyLinkMovmoView,
    demoAirlineLoyaltyLinkErrorCode,
    linkLoyaltyWithSession,
} from "../../services/demoAirlineApi";
import {
    LoyaltyLinkRecord,
    MOCK_LOYALTY_MEMBER,
    WireLoyaltyLink,
    deriveWireLoyaltyLink,
    maskEmail,
    wireLinkToRecord,
    writeLoyaltyGrantAttemptFlag,
} from "../../services/mockLoyalty";

type Step = "choose" | "verifyNumber" | "verifyCode" | "enroll" | "done";

interface LoyaltyLinkModalProps {
    open: boolean;
    onClose: () => void;
    onLinked: (record: LoyaltyLinkRecord) => void;
}

const PA_BLUE = "#0047ba";
const PA_BLUE_HOVER = "#003a96";
const PA_DEEP_BLUE = "#01296a";

// Fake network delay so the "airline verifies you" beat reads as real work
// happening rather than an instant no-op click — this is the "verification
// theater" the ceremony is named for. MOVMO-542: now a MINIMUM duration
// layered over the real POST .../loyalty/link call (see withMinDuration),
// not a replacement for it.
const THEATER_MS = 800;

// MOVMO-542 grant-half-of-late-link (brief item 6): the scope re-requested
// when a successful airline-side link comes back with no `loyalty` group on
// the wire (traveler connected to Movmo before loyalty.read existed, or
// declined that row specifically). Includes profile.read/preferences.read
// too since a scope request is not additive server-side — omitting them
// would ask the traveler to re-decide grants they already made.
const GRANT_LOYALTY_SCOPE = ["profile.read", "preferences.read", "loyalty.read"];

/** Runs `promise` alongside a THEATER_MS delay, resolving with promise's result once BOTH finish — a minimum-duration floor, never a shortcut past the real call. */
function withMinDuration<T>(promise: Promise<T>): Promise<T> {
    const delay = new Promise<void>((resolve) => {
        window.setTimeout(resolve, THEATER_MS);
    });
    return Promise.all([promise, delay]).then(([result]) => result);
}

/**
 * The loyalty link ceremony (MOVMO-541 Layer 2, wired to the real backend in
 * MOVMO-542). Two paths — "link my existing account" (member-number + code,
 * verified by PA's own vendored loyalty adapter) and "join in one click"
 * (instant enrollment) — both call POST /v1/demo/airline/pa/loyalty/link
 * (demoAirlineApi.linkLoyaltyWithSession) and, on success, push the response's
 * wire-legit `movmo` payload into the shared linked-traveler store
 * (applyWireTraveler) so the header flips immediately — no more toast-only
 * success. `onLinked` still exists purely to drive LoyaltySyncToast's copy,
 * and is only called when the wire actually confirms the link (never with
 * fixture data — see services/mockLoyalty.ts's file-header comment).
 */
const LoyaltyLinkModal: React.FC<LoyaltyLinkModalProps> = ({
    open,
    onClose,
    onLinked,
}) => {
    const { traveler } = useLinkedTravelerState("pa");
    const [step, setStep] = useState<Step>("choose");
    const [memberNumber, setMemberNumber] = useState(
        MOCK_LOYALTY_MEMBER.memberNumber,
    );
    const [code, setCode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [inlineError, setInlineError] = useState<string | null>(null);
    // Real wire data from the just-succeeded response — drives the "done"
    // step's display. null + needsLoyaltyGrant true means the airline-side
    // link succeeded but Movmo can't show it yet (missing loyalty.read).
    const [resultLink, setResultLink] = useState<WireLoyaltyLink | null>(null);
    const [needsLoyaltyGrant, setNeedsLoyaltyGrant] = useState(false);

    // Reset the ceremony every time it's (re)opened so a prior run — e.g.
    // done -> close -> reopen — never leaks into the next one.
    useEffect(() => {
        if (open) {
            setStep("choose");
            setMemberNumber(MOCK_LOYALTY_MEMBER.memberNumber);
            setCode("");
            setSubmitting(false);
            setInlineError(null);
            setResultLink(null);
            setNeedsLoyaltyGrant(false);
        }
    }, [open]);

    if (!open) return null;

    const firstName = traveler?.profile?.first_name ?? "there";
    const fullName =
        [traveler?.profile?.first_name, traveler?.profile?.last_name]
            .filter(Boolean)
            .join(" ") || "your Movmo profile";

    /** Shared success handling for both the verify-and-link and enroll actions. */
    const handleLinkSuccess = (
        movmo: DemoAirlineLoyaltyLinkMovmoView,
        path: LoyaltyLinkRecord["path"],
    ) => {
        applyWireTraveler("pa", movmo);
        const wireLink = deriveWireLoyaltyLink(movmo.loyalty);
        setResultLink(wireLink);
        setNeedsLoyaltyGrant(wireLink === null);
        if (wireLink) {
            onLinked(wireLinkToRecord(wireLink, path, true));
        }
        setSubmitting(false);
        setStep("done");
    };

    /** Shared failure handling — inline errors keep the traveler on the current step; 409s close the modal onto the landing page's Movmo chip (shown while the session is unlinked). */
    const handleLinkFailure = (err: unknown, verificationStep: boolean) => {
        setSubmitting(false);
        const code = demoAirlineLoyaltyLinkErrorCode(err);
        if (verificationStep && code === "verification_failed") {
            setInlineError("That code didn't verify — try again.");
            return;
        }
        if (code === "not_connected" || code === "not_recognized") {
            console.warn(
                "[pa] loyalty link needs a Movmo connection first:",
                code,
            );
            onClose();
            return;
        }
        setInlineError("Something went wrong. Please try again.");
    };

    const handleVerifyAndLink = () => {
        setSubmitting(true);
        setInlineError(null);
        withMinDuration(
            linkLoyaltyWithSession("pa", { member_number: memberNumber, code }),
        )
            .then((response) => handleLinkSuccess(response.movmo, "linked"))
            .catch((err) => handleLinkFailure(err, true));
    };

    const handleEnroll = () => {
        setSubmitting(true);
        setInlineError(null);
        withMinDuration(
            linkLoyaltyWithSession("pa", { enroll: true }),
        )
            .then((response) => handleLinkSuccess(response.movmo, "enrolled"))
            .catch((err) => handleLinkFailure(err, false));
    };

    const primaryButtonStyle: React.CSSProperties = {
        backgroundColor: PA_BLUE,
    };

    return (
        <div
            className="fixed inset-0 flex items-center justify-center px-4"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 60 }}
            onClick={onClose}
        >
            <div
                className="w-full rounded-xl bg-white shadow-xl"
                style={{ maxWidth: 440 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 pt-6 pb-5">
                    {step === "choose" && (
                        <>
                            <h2
                                className="text-xl font-bold"
                                style={{ color: PA_DEEP_BLUE }}
                            >
                                Mabuhay Miles
                            </h2>
                            <p className="mt-1 text-sm text-gray-500">
                                Link your account once — Movmo signs you in
                                every time after.
                            </p>

                            <button
                                type="button"
                                onClick={() => setStep("verifyNumber")}
                                className="mt-5 w-full rounded-lg px-4 py-3 text-left text-white transition-colors"
                                style={primaryButtonStyle}
                                onMouseEnter={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                        PA_BLUE_HOVER)
                                }
                                onMouseLeave={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                        PA_BLUE)
                                }
                            >
                                <span className="block text-[15px] font-semibold">
                                    Link my existing account
                                </span>
                            </button>

                            <button
                                type="button"
                                onClick={() => setStep("enroll")}
                                className="mt-3 w-full rounded-lg border border-gray-200 px-4 py-3 text-left text-gray-800 hover:bg-gray-50 transition-colors"
                            >
                                <span className="block text-[15px] font-semibold">
                                    Not a member? Join in one click
                                </span>
                                <span className="mt-0.5 block text-sm text-gray-500">
                                    We&rsquo;ll create your account from your
                                    Movmo profile.
                                </span>
                            </button>
                        </>
                    )}

                    {step === "verifyNumber" && (
                        <>
                            <button
                                type="button"
                                onClick={() => setStep("choose")}
                                className="mb-3 text-sm text-gray-400 hover:text-gray-600"
                            >
                                ← Back
                            </button>
                            <h2
                                className="text-xl font-bold"
                                style={{ color: PA_DEEP_BLUE }}
                            >
                                Verify your account
                            </h2>
                            <label className="mt-4 block text-sm font-medium text-gray-700">
                                Member number
                            </label>
                            {/* text-gray-900 is load-bearing: index.css sets
                                body to text-white (dark-skin default) and
                                Tailwind preflight makes inputs color:inherit,
                                so without it the text is white-on-white. */}
                            <input
                                type="text"
                                value={memberNumber}
                                onChange={(e) =>
                                    setMemberNumber(e.target.value)
                                }
                                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-[15px] text-gray-900 focus:outline-none focus:ring-2"
                                style={{ borderColor: "#d1d5db" }}
                            />
                            <p className="mt-3 text-sm text-gray-500">
                                For your security, Philippine Airlines will
                                verify this is your account.
                            </p>
                            <button
                                type="button"
                                onClick={() => setStep("verifyCode")}
                                className="mt-5 w-full rounded-lg px-4 py-3 text-[15px] font-semibold text-white transition-colors"
                                style={primaryButtonStyle}
                            >
                                Send verification code
                            </button>
                        </>
                    )}

                    {step === "verifyCode" && (
                        <>
                            <button
                                type="button"
                                onClick={() => {
                                    setInlineError(null);
                                    setStep("verifyNumber");
                                }}
                                className="mb-3 text-sm text-gray-400 hover:text-gray-600"
                            >
                                ← Back
                            </button>
                            <h2
                                className="text-xl font-bold"
                                style={{ color: PA_DEEP_BLUE }}
                            >
                                Enter your code
                            </h2>
                            <p className="mt-1 text-sm text-gray-500">
                                We sent a code to{" "}
                                {maskEmail(traveler?.profile?.email)}
                            </p>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={code}
                                onChange={(e) => {
                                    setInlineError(null);
                                    setCode(
                                        e.target.value
                                            .replace(/\D/g, "")
                                            .slice(0, 6),
                                    );
                                }}
                                placeholder="000000"
                                className="mt-3 w-full rounded-md border border-gray-300 px-3 py-2 text-center text-lg tracking-[0.4em] text-gray-900 placeholder:text-gray-300 focus:outline-none focus:ring-2"
                            />

                            {inlineError && (
                                <p
                                    className="mt-3 text-sm"
                                    style={{ color: "#b42318" }}
                                    role="alert"
                                >
                                    {inlineError}
                                </p>
                            )}

                            <p className="mt-4 text-sm text-gray-500">
                                Linking signs you in here through Movmo and
                                keeps your miles and tier ready in your Movmo
                                account. You can unlink anytime.
                            </p>

                            <button
                                type="button"
                                disabled={code.length !== 6 || submitting}
                                onClick={handleVerifyAndLink}
                                className="mt-5 w-full rounded-lg px-4 py-3 text-[15px] font-semibold text-white transition-colors disabled:opacity-40"
                                style={primaryButtonStyle}
                            >
                                {submitting ? "Verifying…" : "Verify and link"}
                            </button>
                        </>
                    )}

                    {step === "enroll" && (
                        <>
                            <button
                                type="button"
                                onClick={() => {
                                    setInlineError(null);
                                    setStep("choose");
                                }}
                                className="mb-3 text-sm text-gray-400 hover:text-gray-600"
                            >
                                ← Back
                            </button>
                            <h2
                                className="text-xl font-bold"
                                style={{ color: PA_DEEP_BLUE }}
                            >
                                Join Mabuhay Miles
                            </h2>
                            <div className="mt-4 rounded-lg bg-gray-50 px-4 py-3">
                                <p className="text-sm text-gray-700">
                                    We&rsquo;ll use your Movmo profile:{" "}
                                    <span className="font-medium">
                                        {fullName}
                                        {traveler?.profile?.email
                                            ? ` · ${traveler.profile.email}`
                                            : ""}
                                    </span>
                                </p>
                            </div>

                            {inlineError && (
                                <p
                                    className="mt-3 text-sm"
                                    style={{ color: "#b42318" }}
                                    role="alert"
                                >
                                    {inlineError}
                                </p>
                            )}

                            <p className="mt-4 text-sm text-gray-500">
                                Your new membership stays synced with your
                                Movmo account. You can unlink anytime.
                            </p>

                            <button
                                type="button"
                                disabled={submitting}
                                onClick={handleEnroll}
                                className="mt-5 w-full rounded-lg px-4 py-3 text-[15px] font-semibold text-white transition-colors disabled:opacity-60"
                                style={primaryButtonStyle}
                            >
                                {submitting
                                    ? "Creating account…"
                                    : "Create my Mabuhay Miles account"}
                            </button>
                        </>
                    )}

                    {step === "done" && resultLink && (
                        <div className="text-center">
                            <div
                                className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
                                style={{ backgroundColor: "#e6f4ea" }}
                            >
                                <span
                                    className="text-2xl"
                                    style={{ color: "#1a7f37" }}
                                    aria-hidden="true"
                                >
                                    ✓
                                </span>
                            </div>
                            <h2
                                className="mt-4 text-xl font-bold"
                                style={{ color: PA_DEEP_BLUE }}
                            >
                                You&rsquo;re all set, {firstName}
                            </h2>
                            <p className="mt-1 text-sm text-gray-500">
                                {resultLink.memberNumber}
                            </p>
                            <p className="mt-3 text-sm text-gray-700">
                                Signed in via Movmo — you won&rsquo;t need a
                                password here again.
                            </p>
                            <button
                                type="button"
                                onClick={onClose}
                                className="mt-5 w-full rounded-lg px-4 py-3 text-[15px] font-semibold text-white transition-colors"
                                style={primaryButtonStyle}
                            >
                                Done
                            </button>
                        </div>
                    )}

                    {/* MOVMO-542 grant-half-of-late-link: the airline-side
                        link succeeded, but Movmo has no loyalty.read grant
                        to show it — offer the delta-consent reconnect right
                        here instead of a silently incomplete "done" state.

                        UNDECIDED-VS-DENIED ASYMMETRY (MOVMO-542
                        denied-scope follow-up, MOVMO-455's consent model):
                        clicking the button below always round-trips
                        through Movmo OAuth, but what the traveler sees
                        depends on whether they've ALREADY decided on
                        loyalty.read for this client. UNDECIDED -> auth-ui
                        shows the delta-consent screen, a grant here flips
                        the wire and the header chip appears on the next
                        /me read (this is the interstitial's intended
                        path). DENIED (traveler explicitly turned it off in
                        their Movmo hub) -> because this request names
                        loyalty.read explicitly (GRANT_LOYALTY_SCOPE above),
                        auth-ui RE-ASKS: the delta-consent screen re-appears
                        with a "previously turned off" label so the
                        traveler can reconsider, rather than completing
                        silently. Only a scope a client never asks for by
                        name is skipped without a prompt. If the traveler
                        declines AGAIN on that re-ask, the round trip still
                        completes with no grant. writeLoyaltyGrantAttemptFlag()
                        below marks that this ceremony was launched so the
                        landing page (PhilippineAirlines.tsx) can tell that
                        re-declined case apart from "never clicked" and
                        show hub guidance instead of leaving a dead end —
                        see mockLoyalty.ts's LOYALTY_GRANT_ATTEMPT_FLAG_KEY
                        comment for the full flag contract. This modal
                        itself does nothing further with that outcome; the
                        traveler has already navigated away by the time it
                        matters. */}
                    {step === "done" && !resultLink && needsLoyaltyGrant && (
                        <div className="text-center">
                            <div
                                className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
                                style={{ backgroundColor: "#fef3e2" }}
                            >
                                <span
                                    className="text-2xl"
                                    style={{ color: "#b54708" }}
                                    aria-hidden="true"
                                >
                                    !
                                </span>
                            </div>
                            <h2
                                className="mt-4 text-xl font-bold"
                                style={{ color: PA_DEEP_BLUE }}
                            >
                                One more step
                            </h2>
                            <p className="mt-1 text-sm text-gray-700">
                                Your Mabuhay Miles account is linked. Allow
                                Philippine Airlines to see your loyalty status
                                through Movmo, so your miles and tier appear
                                whenever Movmo signs you in here.
                            </p>
                            <div
                                className="mt-5 flex justify-center"
                                onClick={writeLoyaltyGrantAttemptFlag}
                            >
                                <ConnectMovmoButton
                                    clientId="pa-demo-airline"
                                    redirectUri={buildRedirectUri("pa")}
                                    scope={GRANT_LOYALTY_SCOPE}
                                    label="Share loyalty status with"
                                    variant="primary"
                                    className="!text-sm"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="mt-4 text-sm text-gray-400 hover:text-gray-600"
                            >
                                Not now
                            </button>
                        </div>
                    )}
                </div>

                <div className="rounded-b-xl border-t border-gray-100 px-6 py-3">
                    <p className="text-[12px] text-gray-400">
                        Demo simulation — verification is performed by the
                        airline, not Movmo.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LoyaltyLinkModal;
